// ============================================================================
// src/pig/pigRecordOps.ts — running a 235 record operation (breeding pig,
// breeding cycle, farrowing record) against a route's local list
// ----------------------------------------------------------------------------
// The 235 counterpart of pigFeederOps.ts. Each surface keeps ONE list of its
// records; an operation's live result names the record it wrote
// (`rows.<rowKey>`) or deleted (`rows.<deletedKey>`), and that is adopted at
// once. A replay, an unknown outcome or a conflict re-reads the surface's
// resource instead: a receipt holds no rows, and a conflict means the local
// copy is stale. Operations on one surface run one after another.
// ============================================================================

import {applyPigOperation, type PigOpOutcome, type PigOpRequest} from '../lib/pigOperationsApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {SaveStatusSink} from '../lib/appStoreBlobApi.js';

type RecordRow = {id?: unknown};
const sameId = (a: unknown, b: unknown) => typeof a === 'string' && typeof b === 'string' && a.trim() === b.trim();

/** The list with a live result's record applied, or null when there is nothing to adopt. */
export function adoptRecordRows<T extends RecordRow>(
  list: T[],
  rows: Record<string, unknown> | null | undefined,
  rowKey: string,
  deletedKey: string,
): T[] | null {
  if (!rows) return null;
  const row = rows[rowKey];
  if (row && typeof row === 'object' && !Array.isArray(row)) {
    const next = row as T;
    const at = list.findIndex((r) => sameId(r.id, next.id));
    if (at < 0) return [...list, next];
    const copy = list.slice();
    copy[at] = next;
    return copy;
  }
  const deleted = rows[deletedKey];
  if (typeof deleted === 'string') {
    const kept = list.filter((r) => !sameId(r.id, deleted));
    return kept.length === list.length ? null : kept;
  }
  return null;
}

export type RecordOpRunnerDeps<T extends RecordRow> = {
  sb: SupabaseClient;
  rowKey: 'breeder' | 'cycle' | 'farrowing';
  deletedKey: 'breeder_id' | 'cycle_id' | 'farrowing_id';
  getRows: () => T[];
  setRows: (next: T[]) => void;
  reread: () => void;
  onSaveStatus: SaveStatusSink;
};

export type RecordOpRunner = (request: PigOpRequest) => Promise<PigOpOutcome>;

export function createRecordOpRunner<T extends RecordRow>(deps: RecordOpRunnerDeps<T>): RecordOpRunner {
  let chain: Promise<unknown> = Promise.resolve();
  return (request) => {
    const run = chain.then(async () => {
      deps.onSaveStatus('saving');
      const outcome = await applyPigOperation(deps.sb, request);
      if (outcome.status === 'saved' || outcome.status === 'no_change' || outcome.status === 'conflict') {
        const adopted = adoptRecordRows(deps.getRows(), outcome.rows, deps.rowKey, deps.deletedKey);
        if (adopted) deps.setRows(adopted);
      }
      const reread =
        outcome.status === 'unknown' ||
        outcome.status === 'conflict' ||
        (outcome.status === 'saved' && (outcome.mustReread || outcome.written.farmBatch !== undefined));
      if (reread) deps.reread();
      const ok = outcome.status === 'saved' || outcome.status === 'no_change';
      deps.onSaveStatus(ok ? 'saved' : 'error');
      setTimeout(() => deps.onSaveStatus(''), ok ? 2500 : 4000);
      return outcome;
    });
    chain = run.catch(() => undefined);
    return run;
  };
}

/** Whether a stored value already means the form's value (absent and null read as ''). */
export function sameRecordValue(stored: unknown, next: unknown): boolean {
  const a = stored == null ? '' : stored;
  const b = next == null ? '' : next;
  if (typeof a === 'number' || typeof b === 'number') return String(a) === String(b);
  return JSON.stringify(a) === JSON.stringify(b);
}
